import { useState } from "react";
import { Icon } from "../components/Icon";
import { Btn, Card, PageHead, Seg, Skeleton, NeedsData, Toggle } from "../components/kit";
import { useMemoryAll, useAudit, useFlags, useConfig, useObservability, useProviderHealth, useRevenue, useReleases, useComms, sendBroadcast, deleteMemory, relTime, shortUser, fmtClockUTC, fmtBytes, type BroadcastResult } from "../lib/admin";

const SkelRows = ({ n = 5, h = 18 }: { n?: number; h?: number }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>
    {Array.from({ length: n }).map((_, i) => <Skeleton key={i} h={h} />)}
  </div>
);

/* ===== Billing & Revenue ===== */
export function RevenuePage() {
  const rev = useRevenue();
  const money = (n: number | null) => (n == null ? "—" : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n));
  return (
    <div className="page">
      <PageHead title="Billing & Revenue" sub="Plan mix from the live account base × Autumn prices — MRR, ARPU and growth." />

      <div className="pulse-grid">
        <Stat icon="banknote" label="MRR" value={rev ? money(rev.mrrUsd) : null} />
        <Stat icon="users" label="ARPU" value={rev ? money(rev.arpuUsd) : null} />
        <Stat icon="credit-card" label="Paying orgs" value={rev ? `${rev.payingOrgs}/${rev.totalOrgs}` : null} />
        <Stat icon="trending-up" label="New orgs · MTD" value={rev ? String(rev.newOrgsThisMonth) : null} />
      </div>

      {rev && !rev.configured && (
        <NeedsData icon="banknote">Plan mix below is live from the account base. <b>MRR / ARPU</b> show “—” because Autumn prices aren’t available (no <code>AUTUMN_SECRET_KEY</code>, or no product prices set) — wire those to value the recurring revenue.</NeedsData>
      )}

      <Card title="Plan mix" icon="layers" pad={false} count={rev ? `${rev.totalOrgs} orgs` : undefined}>
        {rev === null ? (
          <SkelRows n={4} h={22} />
        ) : (
          <div className="gtable">
            <div className="gt-head"><span>Plan</span><span>Orgs</span><span>Members</span><span>Price</span><span>Monthly</span></div>
            {rev.planMix.map((p) => {
              const monthly = p.priceUsd != null && (p.tier === "pro" || p.tier === "team") ? p.priceUsd * p.orgs : null;
              return (
                <div className="gt-row static" key={p.tier} style={{ gridTemplateColumns: "1.4fr 1fr 1fr 1fr 1fr" }}>
                  <span><span className={`plan-pill ${p.tier}`}>{p.label}</span></span>
                  <span className="gt-mono">{p.orgs}</span>
                  <span className="gt-mono">{p.users}</span>
                  <span className="gt-mono">{p.priceUsd != null ? money(p.priceUsd) : "—"}</span>
                  <span className="gt-mono">{monthly != null ? money(monthly) : "—"}</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <Card title="Credit economics" icon="calculator">
        <div className="kv"><span>Sale price</span><b>$0.05 / credit</b></div>
        <div className="kv"><span>Sonnet weight</span><b>1× credit</b></div>
        <div className="kv"><span>Opus weight</span><b>3× credits</b></div>
        <div className="muted-sm" style={{ marginTop: 8 }}>Live values from <code>rates.ts</code> — the real credit weighting the worker charges. Churn / cohort retention still needs a billing-event history (see the status doc).</div>
      </Card>
    </div>
  );
}

/* ===== AI & Voice config ===== */
export function AIPage() {
  const cfg = useConfig();
  // Read-only reference rows (these are client- or env-set, not worker-overridable yet).
  const rows: [string, string, string][] = [
    ["cpu", "Default model", "Claude Sonnet 4.6"],
    ["sparkles", "Opus gating", "Pro & above (operator kill-switch in Cost)"],
    ["ruler", "Max tokens / turn", "1,024"],
    ["zap", "Prompt caching", "Ephemeral system-prompt cache · on"],
    ["ear", "STT (AssemblyAI)", "u3-rt-pro · streaming"],
    ["brain-circuit", "Memory extraction", "Sonnet · after each turn"],
  ];
  return (
    <div className="page">
      <PageHead title="AI & Voice config" sub="Models, voice, transcription and the memory pipeline." />

      <Card title="Voice (Cartesia) — live override" icon="mic" iconColor="var(--g-violet)">
        {cfg === null ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} h={34} />)}</div>
        ) : (
          <VoiceEditor
            voiceId={cfg.platform.cartesiaVoiceId}
            model={cfg.platform.cartesiaModel}
            onSave={(voice, model) => Promise.all([cfg.setConfig("cartesia_voice_id", voice), cfg.setConfig("cartesia_model", model)]).then(() => {})}
          />
        )}
        <div className="muted-sm" style={{ marginTop: 10 }}>Applied at the <code>/tts</code> proxy on the next reply. Leave a field blank to fall back to the deploy default (<code>CARTESIA_VOICE_ID</code> / <code>sonic-3.5</code>).</div>
      </Card>

      <Card title="Reference (read-only)" icon="settings-2" pad={false}>
        <div className="set-list">
          {rows.map((r, i) => (
            <div className="set-row" key={i}><span className="set-ic"><Icon name={r[0]} size={17} /></span><div className="set-body"><div className="set-t">{r[1]}</div><div className="set-d">{r[2]}</div></div></div>
          ))}
        </div>
      </Card>
      <div className="muted-sm">Default model, max-tokens and prompt-caching are set in the desktop client / deploy env — they're shown here for reference. Opus on/off is live in <b>Cost &amp; Margin</b>; the selector mix is editable in <b>Curriculum → Selector</b>.</div>
    </div>
  );
}

function VoiceEditor({ voiceId, model, onSave }: { voiceId: string; model: string; onSave: (voice: string, model: string) => Promise<void> }) {
  const [v, setV] = useState(voiceId);
  const [m, setM] = useState(model);
  const [saving, setSaving] = useState(false);
  const dirty = v !== voiceId || m !== model;
  const save = () => { setSaving(true); onSave(v.trim(), m.trim()).finally(() => setSaving(false)); };
  return (
    <div className="cfg-edit">
      <label className="cfg-field"><span>Voice ID</span><input className="cfg-input" value={v} placeholder="env default" onChange={(e) => setV(e.target.value)} spellCheck={false} /></label>
      <label className="cfg-field"><span>Model</span><input className="cfg-input" value={m} placeholder="sonic-3.5" onChange={(e) => setM(e.target.value)} spellCheck={false} /></label>
      <Btn variant="live" size="sm" icon="check" disabled={!dirty || saving} onClick={save}>{saving ? "Saving…" : "Save"}</Btn>
    </div>
  );
}

/* ===== Memory Inspector ===== */
export function MemoryPage() {
  const { entries, reload } = useMemoryAll();
  const [kind, setKind] = useState("all");
  const kinds = entries ? Array.from(new Set(entries.map((e) => e.kind))) : [];
  const rows = entries ? (kind === "all" ? entries : entries.filter((r) => r.kind === kind)) : [];
  const remove = (id: string) => deleteMemory(id).then(reload).catch(() => {});
  return (
    <div className="page">
      <PageHead title="Memory Inspector" sub="The semantic store (user_memory) across every user — view and delete." />
      {entries && (
        <div className="chip-bar">
          <button className={`chip ${kind === "all" ? "on" : ""}`} onClick={() => setKind("all")}>all</button>
          {kinds.map((k) => <button key={k} className={`chip ${kind === k ? "on" : ""}`} onClick={() => setKind(k)}>{k}</button>)}
        </div>
      )}
      <Card pad={false} count={entries ? `${rows.length} entries` : undefined} title="Entries" icon="brain-circuit" iconColor="var(--g-violet)">
        {entries === null ? (
          <SkelRows n={8} />
        ) : rows.length === 0 ? (
          <div className="gt-empty">No memories stored yet.</div>
        ) : (
          <div className="mem-rows">
            {rows.map((m) => (
              <div className="mem-row wide" key={m.id}>
                <span className="mem-user">{m.user}</span>
                <span className="mem-kind">{m.kind}</span>
                <span className="mem-txt">{m.text}</span>
                <span className="mem-conf">{Math.round(m.conf * 100)}%</span>
                <button className="icon-mini" title="Delete" onClick={() => remove(m.id)}><Icon name="trash-2" size={13} /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ===== Observability ===== */
const STATUS_TONE: Record<string, string> = { up: "var(--g-green)", down: "var(--g-danger)", paused: "var(--g-t3)", unknown: "var(--g-amber)" };
const STATUS_LABEL: Record<string, string> = { up: "Operational", down: "Down", paused: "Probe off", unknown: "No data yet" };

export function ObservabilityPage() {
  const obs = useObservability();
  const health = useProviderHealth();
  const cfg = useConfig();
  const toggleProbe = (id: string, on: boolean) => {
    cfg?.setConfig(`probe_${id}_enabled`, on).then(() => health.reload());
  };
  const peakVolume = obs ? Math.max(1, ...obs.requestVolume.map((v) => v.turns)) : 1;
  return (
    <div className="page">
      <PageHead title="Observability" sub="Request volume, the live turn feed, provider health and per-service cost — all from the worker." />

      <div className="pulse-grid">
        <Stat icon="activity" label="Turns · last hour" value={obs ? obs.turnsLastHour.toLocaleString() : null} />
        <Stat icon="history" label="Turns · 24h" value={obs ? obs.turnsLastDay.toLocaleString() : null} />
        <Stat icon="server" label="Probes live" value={health.providers ? String(health.providers.filter((p) => p.enabled).length) + "/3" : null} />
        <Stat icon="dollar-sign" label="Cost · 24h" value={obs ? "$" + obs.serviceMix.reduce((s, m) => s + m.costUsd, 0).toFixed(2) : null} />
      </div>

      <div className="two-col">
        <Card title="Request volume · 24h" icon="activity" iconColor="var(--g-green)">
          {obs === null ? (
            <Skeleton h={120} r={10} />
          ) : (
            <div className="obs-spark">
              {obs.requestVolume.map((v, i) => (
                <span key={i} className="obs-bar" title={`${v.turns} turns`} style={{ height: Math.max(2, (v.turns / peakVolume) * 100) + "%" }} />
              ))}
            </div>
          )}
          <div className="muted-sm" style={{ marginTop: 8 }}>Hourly turns from <code>turn_audit</code> · peak {peakVolume}/hr.</div>
        </Card>

        <Card title="Provider health" icon="server" pad={false}
          action={<span className="muted-sm">probes every 5 min</span>}>
          {health.providers === null ? (
            <SkelRows n={3} h={26} />
          ) : (
            <div className="set-list">
              {health.providers.map((p) => (
                <div className="set-row" key={p.id}>
                  <span className="set-ic" style={{ color: STATUS_TONE[p.status] }}><span className="health-dot" style={{ background: STATUS_TONE[p.status] }} /></span>
                  <div className="set-body">
                    <div className="set-t">{p.label}</div>
                    <div className="set-d">
                      {STATUS_LABEL[p.status]}
                      {p.enabled && p.status !== "unknown" && <> · {p.latencyMs}ms{p.uptimePct != null ? ` · ${p.uptimePct}% 24h` : ""}</>}
                      {p.status === "down" && p.error ? ` · ${p.error}` : ""}
                    </div>
                  </div>
                  <Toggle on={p.enabled} onChange={(v) => toggleProbe(p.id, v)} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="two-col">
        <Card title="Live turn feed" icon="history" pad={false} count={obs ? `${obs.recentFeed.length} recent` : undefined}>
          {obs === null ? (
            <SkelRows n={6} />
          ) : obs.recentFeed.length === 0 ? (
            <div className="gt-empty">No turns recorded yet.</div>
          ) : (
            <div className="feed-list">
              {obs.recentFeed.map((t, i) => (
                <div className="feed-row" key={i}>
                  <span className="gt-mono feed-time">{fmtClockUTC(t.ts)}</span>
                  <span className="gt-mut feed-user">{shortUser(t.userId)}</span>
                  <span className={`model-pill ${t.model.toLowerCase().includes("opus") ? "opus" : "sonnet"}`}>{t.model.toLowerCase().includes("opus") ? "Opus" : "Sonnet"}</span>
                  <span className={`feed-mode ${t.mode}`}>{t.mode}</span>
                  <span className="gt-mono feed-cr">{t.credits} cr</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Cost by service · 24h" icon="receipt-text" pad={false}>
          {obs === null ? (
            <SkelRows n={3} h={22} />
          ) : obs.serviceMix.length === 0 ? (
            <div className="gt-empty">No cost events in the last 24h.</div>
          ) : (
            <div className="set-list">
              {obs.serviceMix.map((m) => (
                <div className="set-row" key={m.service}>
                  <span className="set-ic"><Icon name={m.service === "llm" ? "cpu" : m.service === "tts" ? "mic" : "ear"} size={16} /></span>
                  <div className="set-body"><div className="set-t" style={{ textTransform: "uppercase" }}>{m.service}</div><div className="set-d">{m.calls.toLocaleString()} calls</div></div>
                  <div className="budget-val">${m.costUsd.toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: string; label: string; value: string | null }) {
  return (
    <div className="pulse">
      <div className="pulse-top"><span className="pulse-ic"><Icon name={icon} size={15} /></span><span className="pulse-label">{label}</span></div>
      <div className="pulse-v">{value === null ? <Skeleton w={60} h={22} /> : value}</div>
    </div>
  );
}

/* ===== Releases ===== */
export function ReleasesPage() {
  const data = useReleases();
  const host = data ? (() => { try { return new URL(data.appcastUrl).host; } catch { return data.appcastUrl; } })() : "";
  return (
    <div className="page">
      <PageHead title="Releases" sub="Desktop app versions, live from the Sparkle appcast the macOS app updates from."
        actions={data ? <a href={data.appcastUrl} target="_blank" rel="noreferrer" className="search-inline g" style={{ textDecoration: "none" }}><Icon name="link" size={14} />{host}</a> : undefined} />

      {data === null ? (
        <SkelRows n={3} h={64} />
      ) : !data.reachable ? (
        <NeedsData icon="upload">Couldn’t read the appcast at <code>{data.appcastUrl}</code>{data.error ? ` — ${data.error}` : ""}. Check the file is published and publicly readable.</NeedsData>
      ) : data.releases.length === 0 ? (
        <div className="gt-empty">The appcast is reachable but lists no releases yet.</div>
      ) : (
        <Card title={`${data.channel} — published versions`} icon="package" count={`${data.releases.length} live`} pad={false}>
          <div className="rel-list">
            {data.releases.map((r, i) => (
              <div className="rel-row" key={r.build || i}>
                <span className="rel-ver">
                  <b>{r.shortVersion}</b>
                  {i === 0 && <span className="rel-latest">Latest</span>}
                </span>
                <div className="rel-meta">
                  <span className="rel-build">build {r.build || "—"}</span>
                  <span className="rel-dot">·</span>
                  <span>{r.pubDateMs ? new Date(r.pubDateMs).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</span>
                  {r.minimumSystemVersion && <><span className="rel-dot">·</span><span>macOS {r.minimumSystemVersion}+</span></>}
                  {r.sizeBytes > 0 && <><span className="rel-dot">·</span><span>{fmtBytes(r.sizeBytes)}</span></>}
                  <span className="rel-dot">·</span>
                  <span className={r.signed ? "rel-signed" : "rel-unsigned"}><Icon name={r.signed ? "shield-check" : "shield-alert"} size={12} />{r.signed ? "signed" : "unsigned"}</span>
                </div>
                <div className="rel-actions">
                  {r.releaseNotes && <a href={r.releaseNotes} target="_blank" rel="noreferrer" className="icon-mini" title="Release notes"><Icon name="file-text" size={14} /></a>}
                  {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="icon-mini" title="Download .dmg"><Icon name="download" size={14} /></a>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="muted-sm">Read-only mirror of the live appcast. Publishing, rollback and kill need write access to the appcast host (R2/S3) — a separate operator action, not wired here.</div>
    </div>
  );
}

/* ===== Feature Flags ===== */
export function FlagsPage() {
  const live = useFlags();
  return (
    <div className="page">
      <PageHead title="Feature Flags" sub="Global flags, kill switches and rollout experiments." />
      <Card pad={false}>
        {live === null ? (
          <SkelRows n={5} h={22} />
        ) : live.flags.length === 0 ? (
          <div className="gt-empty">No flags defined yet.</div>
        ) : (
          <div className="set-list">
            {live.flags.map((f) => (
              <div className={`set-row ${f.key === "maintenance_mode" ? "danger" : ""}`} key={f.key}>
                <span className="set-ic"><Icon name="flag" size={16} /></span>
                <div className="set-body"><div className="set-t mono">{f.key}</div><div className="set-d">{f.desc}</div></div>
                <span className="flag-scope">{f.scope}</span>
                <Toggle on={f.on} onChange={(v) => live.setFlag(f.key, v)} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ===== Comms ===== */
const AUD_MAP: Record<string, string> = { All: "all", Paid: "paid", Free: "free" };
const CHAN_MAP: Record<string, string> = { "In-app": "in-app", Email: "email", Both: "both" };

export function CommsPage() {
  const { broadcasts, emailConfigured, reload } = useComms();
  const [aud, setAud] = useState("All");
  const [chan, setChan] = useState("Both");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);
  const canSend = subject.trim().length > 0 && body.trim().length > 0 && !sending;

  const send = () => {
    if (!canSend) return;
    setSending(true); setResult(null);
    sendBroadcast({ audience: AUD_MAP[aud], channel: CHAN_MAP[chan], subject: subject.trim(), body: body.trim() })
      .then((r) => { setResult(r); setSubject(""); setBody(""); reload(); })
      .catch(() => setResult(null))
      .finally(() => setSending(false));
  };

  return (
    <div className="page">
      <PageHead title="Comms" sub="Broadcasts and targeted nudges across in-app + email." />
      {!emailConfigured && (
        <NeedsData icon="mail">Email isn’t configured yet — in-app delivery works now; email sends are skipped until <code>RESEND_API_KEY</code> is set (<code>wrangler secret put RESEND_API_KEY</code>). Recipients still get the in-app notification.</NeedsData>
      )}
      <div className="two-col">
        <Card title="New broadcast" icon="megaphone" iconColor="var(--g-green)">
          <div className="kv"><span>Audience</span><Seg options={["All", "Paid", "Free"]} value={aud} onChange={setAud} /></div>
          <div className="kv"><span>Channel</span><Seg options={["In-app", "Email", "Both"]} value={chan} onChange={setChan} /></div>
          <input className="cfg-input" style={{ width: "100%", marginBottom: 8 }} placeholder="Subject / title" value={subject} onChange={(e) => setSubject(e.target.value)} spellCheck={false} />
          <textarea className="broadcast-edit" placeholder="Write your announcement…" value={body} onChange={(e) => setBody(e.target.value)} spellCheck={false} />
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10 }}>
            <span className="muted-sm">{result ? `Sent to ${result.recipients} · ${result.inApp} in-app · ${result.emailsSent} emailed${result.emailsSkipped ? ` · ${result.emailsSkipped} skipped` : ""}${result.emailsFailed ? ` · ${result.emailsFailed} failed` : ""}` : "Reaches the selected audience immediately."}</span>
            <Btn variant="live" size="sm" icon="send" disabled={!canSend} onClick={send}>{sending ? "Sending…" : "Send broadcast"}</Btn>
          </div>
        </Card>
        <Card title="Recent broadcasts" icon="history" pad={false} count={broadcasts ? `${broadcasts.length}` : undefined}>
          {broadcasts === null ? (
            <SkelRows n={4} />
          ) : broadcasts.length === 0 ? (
            <div className="gt-empty">No broadcasts sent yet.</div>
          ) : (
            <div className="bc-list">
              {broadcasts.map((b, i) => (
                <div className="bc-row" key={i}>
                  <span className="bc-ic"><Icon name="megaphone" size={15} /></span>
                  <div className="bc-body"><div className="bc-t">{b.subject}</div><div className="bc-d">{b.detail} · {relTime(b.ts)}</div></div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      <Card title="Support &amp; feedback" icon="inbox">
        <NeedsData icon="inbox">No support inbox is wired. Connect a source (Intercom/Front/email-in) or a feedback table to populate this — separate from outbound comms.</NeedsData>
      </Card>
    </div>
  );
}

/* ===== Safety & Audit ===== */
export function AuditPage() {
  const live = useAudit();
  const rows = live ? live.entries.map((e) => ({ who: e.who, action: e.action, target: e.target, reason: e.reason, when: relTime(e.ts) })) : null;
  return (
    <div className="page">
      <PageHead title="Safety & Audit" sub="Every god-mode override — who, what, when, why. Immutable."
        actions={<div className="search-inline g"><Icon name="search" size={15} /><input placeholder="Search actions…" /></div>} />
      <Card title="Super-admin action log" icon="shield-alert" iconColor="var(--g-danger)" count={rows ? `${rows.length} · append-only` : undefined} pad={false}>
        {rows === null ? (
          <SkelRows n={5} h={24} />
        ) : rows.length === 0 ? (
          <div className="gt-empty">No overrides logged yet.</div>
        ) : (
          <div className="audit-list">
            {rows.map((a, i) => (
              <div className="audit" key={i}>
                <span className="audit-rail"><span className="audit-dot" /><span className="audit-line" /></span>
                <div className="audit-body">
                  <div className="audit-t"><b>{a.who}</b> {a.action} <span className="audit-target">{a.target}</span></div>
                  <div className="audit-m"><Icon name="message-square" size={12} />{a.reason} · {a.when}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
